/**
 * SoleadLab 2.0 - Data Point Map Markers Hook
 *
 * Renders monitoring data points as draggable HTML markers on the map.
 * Each marker has a colored circle dot + label showing name and primary value.
 * Dragging a marker updates the data point's coordinates in the monitoring store.
 *
 * Click opens a popup with details. Drag relocates the point.
 * HTML markers sit above raster layers, preventing PNG selection interference.
 *
 * IMPORTANT: Uses Zustand subscribe() instead of React selectors to avoid
 * re-render cascades (React error #185). Marker DOM is synced imperatively.
 */

import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { useMonitoringStore, useMapStore } from '../../stores';
import { useIsReadOnlyViewer, useShareSectionAllowed } from '../../hooks/useShareViewer';
import type { AlarmState } from '@/types/monitoring';

// ============================================
// Constants
// ============================================

const STATE_COLORS: Record<AlarmState, string> = {
  ok: '#27AE60',
  warning: '#F2994A',
  critical: '#EB5757',
  stale: '#9CA3AF',
  no_data: '#6B7280',
};

/** Zoom thresholds for decluttering */
const ZOOM_SHOW_LABELS = 16;  // >= 16: dots + labels
const ZOOM_SHOW_DOTS = 14;    // >= 14: dots only (labels hidden)

// ============================================
// Hook
// ============================================

export default function useDataPointMarkers(
  mapRef: React.RefObject<mapboxgl.Map | null>,
  isMapReady: boolean,
  onPointClick?: (dpId: string, position: { x: number; y: number }) => void,
) {
  // Stores { dpId → { marker, dotEl, labelEl } } — single marker per data point
  const markersRef = useRef<Map<string, { marker: mapboxgl.Marker; dotEl: HTMLDivElement; labelEl: HTMLDivElement }>>(new Map());
  const isDraggingRef = useRef(false);
  const rafRef = useRef<number | null>(null);
  // Ref to keep callback stable across re-renders (event listeners are set once)
  const onPointClickRef = useRef(onPointClick);
  onPointClickRef.current = onPointClick;

  // F-02E-2B: data points belong to the optional monitoring section. Share
  // visitors only get them when the link's exposed_sections includes
  // 'monitoring' — otherwise the hook short-circuits without subscribing,
  // creating no markers and clearing any that were created before the
  // session entered share-viewer mode. Owners and authenticated users
  // always pass. Belt-and-suspenders with projectStore.loadProject which
  // also skips hydrating monitoringStore.projectConfig in this case.
  const isReadOnly = useIsReadOnlyViewer();
  const monitoringAllowed = useShareSectionAllowed('monitoring');
  const skipForShareViewer = isReadOnly && !monitoringAllowed;

  useEffect(() => {
    if (!isMapReady) return;
    if (skipForShareViewer) {
      // Defensive cleanup: if we previously rendered markers (e.g. the
      // session flipped from owner to share viewer mid-mount), remove them.
      for (const entry of markersRef.current.values()) {
        entry.marker.remove();
      }
      markersRef.current.clear();
      return;
    }

    // Imperative marker sync — reads store state directly, never triggers React re-renders
    function syncMarkers() {
      const map = mapRef.current;
      if (!map) return;

      const { projectConfig, runtimeStates } = useMonitoringStore.getState();
      const datapoints = projectConfig?.datapoints ?? [];
      const mapState = useMapStore.getState();
      const layerVisible = mapState.layerVisibility.dataPoints;
      const alarmFilters = mapState.dataPointAlarmFilters;
      const labelsOn = mapState.dataPointShowLabels;
      const zoom = map.getZoom();
      const showDots = layerVisible && zoom >= ZOOM_SHOW_DOTS;
      const showLabels = layerVisible && zoom >= ZOOM_SHOW_LABELS && labelsOn;

      // Update the GeoJSON source (used for layer ordering, not rendering)
      const source = map.getSource('data-points') as mapboxgl.GeoJSONSource;
      if (source) {
        source.setData({
          type: 'FeatureCollection',
          features: datapoints
            .filter((dp) => dp.enabled && dp.coordinates)
            .map((dp) => {
              const runtime = runtimeStates.get(dp.id);
              const state: AlarmState = runtime?.state ?? 'no_data';
              return {
                type: 'Feature' as const,
                geometry: { type: 'Point' as const, coordinates: dp.coordinates! },
                properties: { id: dp.id, color: STATE_COLORS[state] },
              };
            }),
        });
      }

      const currentIds = new Set<string>();

      for (const dp of datapoints) {
        if (!dp.enabled || !dp.coordinates) continue;
        currentIds.add(dp.id);

        const runtime = runtimeStates.get(dp.id);
        const state: AlarmState = runtime?.state ?? 'no_data';
        const color = STATE_COLORS[state];
        const labelText = dp.display_name;
        const displayMetric = dp.ui?.display_metric ?? '';
        // Determine which metrics to show: "" = none, "__all__" = all selected, specific = just that one
        let metricsToShow: string[] = [];
        if (displayMetric === '__all__') {
          metricsToShow = dp.selected_metrics;
        } else if (displayMetric) {
          metricsToShow = [displayMetric];
        }
        // Build triggered metric → color map
        const triggeredMetricColors = new Map<string, string>();
        for (const tr of runtime?.triggered_rules ?? []) {
          triggeredMetricColors.set(tr.metric, STATE_COLORS[tr.severity]);
        }

        const valueLines: { key: string; text: string; color: string }[] = [];
        for (const metricKey of metricsToShow) {
          const val = runtime?.values[metricKey];
          if (val !== null && val !== undefined) {
            const formatted = typeof val === 'number' ? val.toFixed(dp.decimals ?? 2) : String(val);
            const metricUnit = dp.metric_units?.[metricKey] ?? dp.unit ?? '';
            const lineColor = triggeredMetricColors.get(metricKey) ?? STATE_COLORS.ok;
            valueLines.push({ key: metricKey, text: `${formatted}${metricUnit ? ' ' + metricUnit : ''}`, color: lineColor });
          }
        }
        const valueText = valueLines.length === 1
          ? valueLines[0].text
          : '';

        const existing = markersRef.current.get(dp.id);

        if (existing) {
          // Update existing marker — skip coordinate update while dragging
          if (!isDraggingRef.current) {
            existing.marker.setLngLat(dp.coordinates as [number, number]);
          }

          // Update dot styles
          const { dotEl, labelEl } = existing;
          dotEl.style.background = color;
          dotEl.style.borderColor = state === 'critical' ? '#c0392b' : state === 'warning' ? '#e67e22' : '#fff';
          dotEl.className = `dp-dot-marker${state === 'warning' ? ' dp-dot--warning' : state === 'critical' ? ' dp-dot--critical' : ''}`;

          // Update label content
          const newHTML = createLabelHTML(labelText, valueText, state, valueLines);
          if (labelEl.innerHTML !== newHTML) {
            labelEl.innerHTML = newHTML;
          }

          // Toggle visibility — zoom-dependent + alarm filter
          const showPoint = showDots && alarmFilters[state] !== false;
          existing.marker.getElement().style.display = showPoint ? '' : 'none';
          existing.labelEl.style.display = showPoint && showLabels ? '' : 'none';
        } else {
          // ── Create single combined marker (dot + label) ──
          // A zero-size wrapper positions both children via absolute layout,
          // so the geo coordinate always maps to the dot center.
          const showPoint = showDots && alarmFilters[state] !== false;
          const wrapperEl = document.createElement('div');
          wrapperEl.style.width = '0';
          wrapperEl.style.height = '0';
          wrapperEl.style.display = showPoint ? '' : 'none';

          // Dot — centered on the geo coordinate via absolute offset
          const dotEl = document.createElement('div');
          dotEl.className = `dp-dot-marker${state === 'warning' ? ' dp-dot--warning' : state === 'critical' ? ' dp-dot--critical' : ''}`;
          dotEl.style.position = 'absolute';
          dotEl.style.width = '20px';
          dotEl.style.height = '20px';
          dotEl.style.left = '-10px';
          dotEl.style.top = '-10px';
          dotEl.style.background = color;
          dotEl.style.borderColor = state === 'critical' ? '#c0392b' : state === 'warning' ? '#e67e22' : '#fff';

          // Label — positioned to the right of the dot, vertically centered
          const labelEl = document.createElement('div');
          labelEl.className = 'dp-map-label';
          labelEl.style.position = 'absolute';
          labelEl.style.left = '14px';
          labelEl.style.top = '0';
          labelEl.style.transform = 'translate3d(0, -50%, 0)';
          labelEl.innerHTML = createLabelHTML(labelText, valueText, state, valueLines);
          labelEl.style.display = showPoint && showLabels ? '' : 'none';

          wrapperEl.appendChild(dotEl);
          wrapperEl.appendChild(labelEl);

          const marker = new mapboxgl.Marker({
            element: wrapperEl,
            draggable: true,
            anchor: 'center',
          })
            .setLngLat(dp.coordinates as [number, number])
            .addTo(map);

          // Drag handlers
          const dpId = dp.id;

          marker.on('dragstart', () => {
            isDraggingRef.current = true;
            dotEl.style.cursor = 'grabbing';
          });

          marker.on('dragend', () => {
            const ll = marker.getLngLat();
            const newCoords: [number, number] = [ll.lng, ll.lat];

            // Update in monitoring store — isDraggingRef stays true until after
            // the store update to prevent the sync from moving the marker back
            useMonitoringStore.getState().updateDataPointConfig(dpId, {
              coordinates: newCoords,
            });

            // Reset drag state after a tick so the sync triggered by the store
            // update above won't revert the marker position
            requestAnimationFrame(() => {
              isDraggingRef.current = false;
              dotEl.style.cursor = 'grab';
            });
          });

          // Click handler — open detail popup (only if not dragging)
          dotEl.addEventListener('click', (e) => {
            if (isDraggingRef.current) return;
            e.stopPropagation();
            onPointClickRef.current?.(dpId, { x: e.clientX, y: e.clientY });
          });

          markersRef.current.set(dp.id, { marker, dotEl, labelEl });
        }
      }

      // Remove stale markers
      for (const [id, entry] of markersRef.current) {
        if (!currentIds.has(id)) {
          entry.marker.remove();
          markersRef.current.delete(id);
        }
      }
    }

    // Batched sync — coalesce rapid store updates into one rAF frame
    function scheduleSync() {
      if (rafRef.current !== null) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        syncMarkers();
      });
    }

    // Initial sync
    syncMarkers();

    // Subscribe to store changes — fires imperatively, no React re-renders
    const unsubMon = useMonitoringStore.subscribe(scheduleSync);
    // React to dataPoints visibility, alarm filters, and labels toggle
    const unsubMap = useMapStore.subscribe(
      (state, prevState) => {
        if (state.layerVisibility.dataPoints !== prevState.layerVisibility.dataPoints ||
            state.dataPointAlarmFilters !== prevState.dataPointAlarmFilters ||
            state.dataPointShowLabels !== prevState.dataPointShowLabels) {
          scheduleSync();
        }
      }
    );

    // Re-sync on zoom change — coalesced via rAF in scheduleSync
    const map = mapRef.current!;
    map.on('zoom', scheduleSync);

    return () => {
      unsubMon();
      unsubMap();
      map.off('zoom', scheduleSync);
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      // Cleanup markers
      for (const entry of markersRef.current.values()) {
        entry.marker.remove();
      }
      markersRef.current.clear();
    };
  }, [isMapReady, skipForShareViewer]);
}

// ============================================
// Helpers
// ============================================

function createLabelHTML(
  name: string,
  value: string,
  state: AlarmState,
  valueLines?: { key: string; text: string; color: string }[],
): string {
  const color = STATE_COLORS[state];
  let valuesHTML = '';
  if (valueLines && valueLines.length > 1) {
    // Multiple metrics — each value colored by its own alarm state
    valuesHTML = valueLines.map(v =>
      `<div style="display:flex;justify-content:space-between;gap:8px;font-size:11px;">` +
      `<span style="color:#666;font-weight:500;">${v.key}</span>` +
      `<span style="font-weight:700;color:${v.color};">${v.text}</span></div>`
    ).join('');
  } else if (value && valueLines?.length === 1) {
    valuesHTML = `<div style="font-weight: 700; color: ${valueLines[0].color}; font-size: 12px;">${value}</div>`;
  } else if (value) {
    valuesHTML = `<div style="font-weight: 700; color: ${color}; font-size: 12px;">${value}</div>`;
  }
  return `
    <div style="
      background: rgba(255,255,255,0.95);
      border: 1px solid ${color};
      border-radius: 4px;
      padding: 2px 6px;
      font-size: 11px;
      font-family: var(--font-main, Inter, sans-serif);
      white-space: nowrap;
      pointer-events: none;
      box-shadow: 0 1px 4px rgba(0,0,0,.12);
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      text-rendering: geometricPrecision;
    ">
      <div style="font-weight: 600; color: #333; font-size: 10px;">${name}</div>
      ${valuesHTML}
    </div>
  `;
}
